import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssignLead, useLeadAssignableManagers } from '@magimp/shared-hooks';
import { Button, FormRow, Select } from '../../../../ui/index.js';
import { errorMessage } from '../../../../api/errors.js';
import { useToastStore } from '../../../../stores/toast.js';

export interface LeadAssignmentControlProps {
  leadId: string;
}

export function LeadAssignmentControl({ leadId }: LeadAssignmentControlProps) {
  const { t } = useTranslation(['leads', 'common']);
  const show = useToastStore((s) => s.show);
  const roster = useLeadAssignableManagers();
  const assign = useAssignLead(leadId);
  const [managerId, setManagerId] = useState('');

  if (roster.error) {
    return (
      <span role="alert" style={{ color: 'var(--status-error-fg)', fontSize: 'var(--fz-12)' }}>
        {errorMessage(roster.error)}
      </span>
    );
  }

  const managers = roster.data?.managers ?? [];
  const placeholder = roster.isPending
    ? t('common:states.loading')
    : managers.length === 0
      ? t('leads:admin.noAssignableManagers')
      : t('leads:admin.pickManager');

  const onAssign = () => {
    if (!managerId) return;
    assign.mutate(
      { managerId },
      {
        onSuccess: () => {
          setManagerId('');
          show({ tone: 'success', text: t('leads:toasts.reassigned') });
        },
        onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
      },
    );
  };

  return (
    <div
      data-testid={`lead-assignment-${leadId}`}
      style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--s-3)', flexWrap: 'wrap' }}
    >
      <FormRow label={t('leads:fields.manager')}>
        <Select
          value={managerId}
          onChange={setManagerId}
          options={managers.map((manager) => ({ value: manager.id, label: manager.name }))}
          placeholder={placeholder}
          disabled={roster.isPending || managers.length === 0 || assign.isPending}
        />
      </FormRow>
      <Button
        kind="primary"
        size="sm"
        onClick={onAssign}
        disabled={!managerId || assign.isPending}
        data-testid={`lead-assign-submit-${leadId}`}
        style={{ marginBottom: 'var(--s-4)' }}
      >
        {t('leads:admin.reassign')}
      </Button>
    </div>
  );
}
